/**
 * The arrivals queue — settling new work against the projection that predicted
 * it (product brief §5, "Projection-to-actual association").
 *
 * Modelled on bank-feed reconciliation, which the brief names deliberately: a
 * list of things that have arrived, each paired with what the system thinks it
 * matches, confirmed one tap at a time, with what you haven't done yet visibly
 * queuing. Reps who use Xero or Sage already know this shape.
 *
 * ## What is actually being reconciled
 *
 * The double-count is not a future risk, it is live on the screen. An arriving
 * quote already sits in the client's book carrying its weighted value; an
 * arriving order has already moved into the committed layers. The rep's
 * projection that predicted that same work is *also* still counted. Both are in
 * the forecast right now.
 *
 * - **Absorb** shrinks the projection by the arrival's face value. The claim was
 *   about work, not weighted work, so £19k of quote answers £19k of a £45k
 *   claim. This is the default, and it lowers the forecast.
 * - **Add on top** leaves the projection alone, on the rep's assertion that this
 *   is extra work beyond what they predicted. It keeps the forecast where it is,
 *   which is exactly why it is the one that asks for a reason.
 *
 * Absorb needs no reason and add does: confirming the system's expectation is
 * one tap, departing from it is the thing worth recording. That asymmetry is
 * what keeps the queue clearable in the minute it deserves, and it is the same
 * bargain a bank feed makes — confirm a match in one click, create a new
 * transaction and you fill a form.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { ArrowLeft, Check, CornerDownRight, Plus, Undo2 } from "lucide-react";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import { usePlatformCapabilities } from "@shared-core/capabilities/context";
import type {
  ArrivalResolution,
  MyPipelineResponse,
  PipelineArrival,
} from "@shared-core/data/my-pipeline-types";
import {
  formatCurrencyGbpCompact,
  formatShortDateOrDash,
} from "@shared-core/domain/formatters";
import {
  applyIntervention,
  measureMove,
  type Actor,
} from "@shared-core/domain/my-pipeline-interventions";
import { paths } from "@shared-core/routing/routes";
import { Badge } from "../app/components/primitives/badge";
import { Button } from "../app/components/primitives/button";
import { Card } from "../app/components/primitives/card";
import { Input } from "../app/components/primitives/input";
import { WorkspaceNav } from "../app/components/domain/forecasting/WorkspaceNav";
import { currentTime } from "@shared-core/domain/current-time";

/** One settled arrival, kept so the pass can be undone and reviewed. */
interface Settled {
  arrival: PipelineArrival;
  resolution: ArrivalResolution;
  note: string;
  before: MyPipelineResponse;
}

export function ArrivalsQueuePage() {
  const adapter = useDataAdapter();
  const capabilities = usePlatformCapabilities();
  const navigate = useNavigate();
  // Present when a manager is settling a report's arrivals rather than their
  // own. Carried through the read and both back-links so the queue and the
  // book it returns to are the same person's.
  const { repId } = useParams<{ repId?: string }>();

  const [data, setData] = useState<MyPipelineResponse | null>(null);
  const [baseline, setBaseline] = useState<MyPipelineResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [settled, setSettled] = useState<Settled[]>([]);
  /** The arrival the rep has opened the "add on top" reason box for. */
  const [addingFor, setAddingFor] = useState<string | null>(null);
  const [note, setNote] = useState("");
  const [focusIndex, setFocusIndex] = useState(0);
  const [resolveError, setResolveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const pipeline = await adapter.getMyPipeline(repId);
        if (cancelled) return;
        setData(pipeline);
        setBaseline(pipeline);
      } catch (err) {
        if (!cancelled) {
          setError(
            err instanceof Error ? err.message : "Could not load your arrivals",
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [adapter, repId]);

  const repName = data?.rep_name;
  const asOfDate = data?.as_of_date;
  const actor: Actor = useMemo(
    () => ({
      name: repName ?? "You",
      today: asOfDate ?? currentTime().toISOString().slice(0, 10),
    }),
    [repName, asOfDate],
  );

  // Memoised because the keyboard effect depends on it — a fresh [] each render
  // would re-register the listener on every render.
  const queue = useMemo(() => data?.arrivals ?? [], [data?.arrivals]);

  /**
   * Settle one arrival: optimistic update, then the real write, then a refetch.
   *
   * A resolution is a single mutable fact about an arriving document, so there
   * is no un-resolve route and no undo offered here. A failed write puts the
   * queue back where it was and says so.
   */
  const resolve = useCallback(
    (arrival: PipelineArrival, resolution: ArrivalResolution, why: string) => {
      const before = data;
      if (!before) return;
      const next = applyIntervention(
        before,
        {
          kind: "arrival",
          clientId: arrival.client_id,
          arrivalId: arrival.id,
        },
        {
          kind: "resolve_arrival",
          arrivalId: arrival.id,
          resolution,
          note: why,
        },
        actor,
      );
      setData(next);
      setAddingFor(null);
      setNote("");
      setFocusIndex(0);
      setResolveError(null);
      setSaving(true);
      void (async () => {
        try {
          await adapter.resolveArrival(arrival.id, resolution, why);
          setData(await adapter.getMyPipeline(repId));
          setSettled((prev) => [
            { arrival, resolution, note: why, before },
            ...prev,
          ]);
        } catch (err) {
          setData(before);
          setResolveError(
            err instanceof Error
              ? err.message
              : "That arrival could not be settled.",
          );
        } finally {
          setSaving(false);
        }
      })();
    },
    [actor, adapter, data, repId],
  );

  const undo = useCallback(() => {
    setSettled((prev) => {
      const [last, ...rest] = prev;
      if (!last) return prev;
      setData(last.before);
      return rest;
    });
  }, []);

  /** Clear the whole queue the honest way — the bank-feed "OK all" move. */
  const absorbAll = useCallback(() => {
    setData((current) => {
      if (!current) return current;
      let next = current;
      const done: Settled[] = [];
      for (const arrival of current.arrivals) {
        done.unshift({ arrival, resolution: "absorb", note: "", before: next });
        next = applyIntervention(
          next,
          {
            kind: "arrival",
            clientId: arrival.client_id,
            arrivalId: arrival.id,
          },
          {
            kind: "resolve_arrival",
            arrivalId: arrival.id,
            resolution: "absorb",
            note: "",
          },
          actor,
        );
      }
      setSettled((prev) => [...done, ...prev]);
      return next;
    });
  }, [actor]);

  // Keyboard: the queue is a list you work down, so it gets the same treatment
  // as the book — a for absorb, t for add on top, u to undo.
  useEffect(() => {
    return capabilities.registerGlobalKeydownListener((event) => {
      if (event.hasModifier || event.fromTextEntry) return;
      if (event.key === "j" || event.key === "ArrowDown") {
        event.preventDefault();
        setFocusIndex((i) => Math.min(i + 1, queue.length - 1));
      } else if (event.key === "k" || event.key === "ArrowUp") {
        event.preventDefault();
        setFocusIndex((i) => Math.max(i - 1, 0));
      } else if (event.key === "u") {
        event.preventDefault();
        undo();
      } else if (event.key === "a" && queue[focusIndex]) {
        event.preventDefault();
        resolve(queue[focusIndex], "absorb", "");
      } else if (event.key === "t" && queue[focusIndex]) {
        event.preventDefault();
        setAddingFor(queue[focusIndex].id);
      }
    });
  }, [capabilities, queue, focusIndex, resolve, undo]);

  if (loading) {
    return (
      <PageFrame>
        <div className="h-40 animate-pulse rounded-lg border border-border bg-surface-subtle" />
      </PageFrame>
    );
  }

  if (error || !data || !baseline) {
    return (
      <PageFrame>
        <Card className="p-6">
          <h2 className="font-semibold">Could not load your arrivals</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            {error ?? "No data returned."}
          </p>
        </Card>
      </PageFrame>
    );
  }

  const pass = measureMove(
    baseline.projected_unbooked,
    data.projected_unbooked,
  );

  return (
    <PageFrame>
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <Link
            to={repId ? paths.repPipeline({ repId }) : paths.myPipeline()}
            className="mb-1 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="size-3.5" />
            My Pipeline
          </Link>
          <h1 className="text-xl font-semibold">Arrivals to settle</h1>
          <p className="max-w-2xl text-sm text-muted-foreground">
            New work has come in against clients where you raised the projection
            yourself. Right now it is counted twice — once as the work, once as
            your prediction of it. Settling each one takes a copy out.
          </p>
        </div>
        {queue.length > 1 && (
          <Button variant="outline" onClick={absorbAll} disabled={saving}>
            <Check className="size-3.5" />
            Absorb all {queue.length}
          </Button>
        )}
      </header>

      {resolveError && (
        <div
          role="alert"
          className="border-destructive bg-card rounded-lg border px-4 py-2.5 text-sm"
        >
          <span className="font-medium">Not settled.</span> {resolveError}
        </div>
      )}

      <PassStrip data={data} pass={pass} settledCount={settled.length} />

      {queue.length === 0 ? (
        <Cleared
          settledCount={settled.length}
          onBack={() =>
            navigate(repId ? paths.repPipeline({ repId }) : paths.myPipeline())
          }
        />
      ) : (
        <ul className="space-y-3">
          {queue.map((arrival, i) => (
            <ArrivalRow
              key={arrival.id}
              arrival={arrival}
              focused={i === focusIndex}
              adding={addingFor === arrival.id}
              note={note}
              onNote={setNote}
              onFocus={() => setFocusIndex(i)}
              onAbsorb={() => resolve(arrival, "absorb", "")}
              onStartAdd={() => {
                setAddingFor(arrival.id);
                setNote("");
              }}
              onCancelAdd={() => setAddingFor(null)}
              onConfirmAdd={() => resolve(arrival, "add", note.trim())}
            />
          ))}
        </ul>
      )}

      {settled.length > 0 && <SettledList settled={settled} onUndo={undo} />}

      <p className="text-center text-xs text-muted-foreground">
        <Kbd>j</Kbd> <Kbd>k</Kbd> move · <Kbd>a</Kbd> absorb · <Kbd>t</Kbd> add
        on top · <Kbd>u</Kbd> undo
      </p>
    </PageFrame>
  );
}

function PageFrame({ children }: { children: React.ReactNode }) {
  return (
    <div className="mx-auto w-full max-w-4xl space-y-4 p-4 pb-20">
      <WorkspaceNav active="pipeline" />
      {children}
    </div>
  );
}

function PassStrip({
  data,
  pass,
  settledCount,
}: {
  data: MyPipelineResponse;
  pass: ReturnType<typeof measureMove>;
  settledCount: number;
}) {
  return (
    <Card className="flex flex-wrap items-baseline gap-x-6 gap-y-2 p-4">
      <div>
        <div className="text-xl font-semibold tabular-nums">
          {formatCurrencyGbpCompact(data.projected_unbooked)}
        </div>
        <div className="text-xs text-muted-foreground">
          projected unbooked, {data.month_label}
        </div>
      </div>
      {pass.delta !== 0 && (
        <div className="text-sm text-muted-foreground">
          <span className="tabular-nums text-foreground">
            {pass.delta > 0 ? "+" : "−"}
            {formatCurrencyGbpCompact(Math.abs(pass.delta))}
          </span>{" "}
          since you started · {settledCount} settled
        </div>
      )}
      <div className="ml-auto text-xs text-muted-foreground">
        Anything you leave is absorbed at month end, not carried forward.
      </div>
    </Card>
  );
}

/**
 * One arrival, paired with the projection it would settle against.
 *
 * The row is deliberately two-sided like a bank feed: what arrived on the left,
 * what it matches on the right, and the consequence of each choice stated in £
 * before either is taken.
 */
function ArrivalRow({
  arrival,
  focused,
  adding,
  note,
  onNote,
  onFocus,
  onAbsorb,
  onStartAdd,
  onCancelAdd,
  onConfirmAdd,
}: {
  arrival: PipelineArrival;
  focused: boolean;
  adding: boolean;
  note: string;
  onNote: (v: string) => void;
  onFocus: () => void;
  onAbsorb: () => void;
  onStartAdd: () => void;
  onCancelAdd: () => void;
  onConfirmAdd: () => void;
}) {
  const excess = arrival.value - arrival.absorbs;
  const consumedPct = arrival.unseen_claimed
    ? Math.min(
        100,
        ((arrival.unseen_claimed - arrival.unseen_remaining) /
          arrival.unseen_claimed) *
          100,
      )
    : 0;
  const absorbPct = arrival.unseen_claimed
    ? Math.min(
        100 - consumedPct,
        (arrival.absorbs / arrival.unseen_claimed) * 100,
      )
    : 0;

  return (
    <li>
      <Card
        onMouseEnter={onFocus}
        className={`overflow-hidden p-0 ${
          focused ? "ring-1 ring-primary/40" : ""
        }`}
      >
        <div className="grid gap-0 md:grid-cols-[1fr_1fr]">
          {/* What arrived */}
          <div className="p-4">
            <div className="flex items-center gap-2">
              <Badge variant={arrival.kind === "order" ? "success" : "neutral"}>
                {arrival.kind === "order" ? "Order" : "Quote"}
              </Badge>
              <span className="font-mono text-xs text-muted-foreground">
                {arrival.reference}
              </span>
              <span className="text-xs text-muted-foreground">
                {formatShortDateOrDash(arrival.arrived_at)}
              </span>
            </div>
            <div className="mt-1 font-medium">{arrival.client_name}</div>
            <div className="text-sm text-muted-foreground">
              {arrival.description}
            </div>
            <div className="mt-2 text-lg font-semibold tabular-nums">
              {formatCurrencyGbpCompact(arrival.value)}
            </div>
          </div>

          {/* What it matches */}
          <div className="border-t border-border bg-surface-subtle p-4 md:border-l md:border-t-0">
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <CornerDownRight className="size-3.5" />
              Matches your projection for {arrival.client_name}
            </div>
            <p className="mt-1 text-sm italic text-muted-foreground">
              &ldquo;{arrival.unseen_reason}&rdquo;
            </p>

            {/* The decay bar: claimed, already consumed, and what this eats. */}
            <div className="mt-2.5 flex h-2 w-full overflow-hidden rounded-full bg-border">
              <div
                className="h-full bg-muted-foreground/40"
                style={{ width: `${consumedPct}%` }}
              />
              <div
                className="h-full bg-primary"
                style={{ width: `${absorbPct}%` }}
              />
            </div>
            <div className="mt-1.5 text-xs text-muted-foreground">
              {formatCurrencyGbpCompact(arrival.unseen_claimed)} claimed ·{" "}
              {formatCurrencyGbpCompact(arrival.unseen_remaining)} still
              unmatched
              {excess > 0 && (
                <>
                  {" "}
                  · only {formatCurrencyGbpCompact(arrival.absorbs)} of this can
                  be absorbed
                </>
              )}
            </div>
          </div>
        </div>

        {/* The choice */}
        <div className="flex flex-wrap items-center gap-2 border-t border-border px-4 py-2.5">
          <Button size="sm" onClick={onAbsorb}>
            <Check className="size-3.5" />
            Absorb
          </Button>
          <span className="text-xs text-muted-foreground">
            This is the work you predicted. Projection drops{" "}
            {formatCurrencyGbpCompact(arrival.absorbs)}
            {excess > 0 && (
              <> and {formatCurrencyGbpCompact(excess)} stays on as extra</>
            )}
            .
          </span>
          <Button
            size="sm"
            variant="ghost"
            className="ml-auto"
            onClick={onStartAdd}
          >
            <Plus className="size-3.5" />
            Add on top
          </Button>
        </div>

        {adding && (
          <div className="space-y-2 border-t border-border bg-amber-50 px-4 py-3 dark:bg-amber-950/30">
            <p className="text-sm">
              Adding on top keeps your projection at{" "}
              <strong className="tabular-nums">
                {formatCurrencyGbpCompact(arrival.unseen_remaining)}
              </strong>{" "}
              and leaves this work counted separately &mdash; so the month goes
              up. Worth saying why.
            </p>
            <div className="flex flex-wrap items-center gap-2">
              <Input
                autoFocus
                value={note}
                maxLength={120}
                placeholder="This is on top of what I predicted, because…"
                onChange={(e) => onNote(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && note.trim()) onConfirmAdd();
                  if (e.key === "Escape") onCancelAdd();
                }}
                className="h-8 max-w-md flex-1 text-sm"
              />
              <Button size="sm" disabled={!note.trim()} onClick={onConfirmAdd}>
                Add on top
              </Button>
              <Button size="sm" variant="ghost" onClick={onCancelAdd}>
                Cancel
              </Button>
            </div>
          </div>
        )}
      </Card>
    </li>
  );
}

function Cleared({
  settledCount,
  onBack,
}: {
  settledCount: number;
  onBack: () => void;
}) {
  return (
    <div className="rounded-lg border border-dashed border-border px-4 py-12 text-center">
      <p className="font-medium">
        {settledCount > 0 ? "Queue cleared." : "Nothing to settle."}
      </p>
      <p className="mx-auto mt-1 max-w-md text-sm text-muted-foreground">
        {settledCount > 0
          ? "Every arrival has been matched against the projection that predicted it, so nothing is counted twice."
          : "No new work has arrived against a client whose projection you adjusted."}
      </p>
      <Button className="mt-4" variant="outline" onClick={onBack}>
        Back to My Pipeline
      </Button>
    </div>
  );
}

function SettledList({
  settled,
  onUndo,
}: {
  settled: Settled[];
  onUndo: () => void;
}) {
  return (
    <Card className="p-4">
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-sm font-semibold">
          Settled this session ({settled.length})
        </h2>
        <Button size="sm" variant="secondary" onClick={onUndo}>
          <Undo2 className="size-3.5" />
          Undo last
        </Button>
      </div>
      <ul className="mt-2 space-y-1 text-sm text-muted-foreground">
        {settled.map((s, i) => (
          <li key={`${s.arrival.id}-${i}`}>
            <span className="font-mono text-xs">{s.arrival.reference}</span>{" "}
            {s.arrival.client_name} ·{" "}
            {s.resolution === "absorb" ? (
              <>absorbed {formatCurrencyGbpCompact(s.arrival.absorbs)}</>
            ) : (
              <>
                added on top
                {s.note && <> — &ldquo;{s.note}&rdquo;</>}
              </>
            )}
          </li>
        ))}
      </ul>
    </Card>
  );
}

function Kbd({ children }: { children: React.ReactNode }) {
  return (
    <kbd className="rounded border border-border bg-surface-subtle px-1 py-0.5 font-mono text-[10px]">
      {children}
    </kbd>
  );
}
